import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Chip,
  Card,
  CardContent,
  useTheme,
  Alert,
} from "@mui/material";

import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import VideoPlayer from "../../../components/VideoPlayer/VideoPlayer";
import BackButton from "../../../components/BackButton/BackButton";
import TrainingInfoBox from "./TrainingInfoBox";
import { TrainingSectionAttemptStatusKey } from "../utils/enums";

const VideoPlayerPage = ({ section, onUpdate, refetch, completedStatusId }) => {
  const theme = useTheme();
  const [isCompleted, setIsCompleted] = useState(false);
  const [isChecked, setIsChecked] = useState(
    section?.progress_status_key === TrainingSectionAttemptStatusKey.COMPLETED
  );
  const [updating, setUpdating] = useState(false);

  const videoUrl = `${process.env.REACT_APP_POLICY_DOC_URL}/${section?.path}`;

  const handleEnded = () => {
    setIsCompleted(true);
  };

  const handleUpdateStatus = async () => {
    setUpdating(true);
    try {
      const success = await onUpdate(section?.id, completedStatusId);
      if (success) {
        await refetch();
      }
    } catch {
      console.error("Error in updating the section status");
    } finally {
      setUpdating(false);
    }
  };

  const isTrainingCompleted =
    section?.progress_status_key === TrainingSectionAttemptStatusKey.COMPLETED;

  return (
    <Box sx={{ maxWidth: "1000px", margin: "0 auto", pb: 4 }}>
      <BackButton label="Back to Training" sx={{ mb: 2 }} />

      {/* Header Card */}
      <Card
        variant="outlined"
        sx={{
          mb: 3,
          borderRadius: "12px",
          border: `1px solid ${theme.customColors?.border?.strong || "#E0E0E0"}`,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor:
              theme.customColors?.background?.cardHeader || "#F7F7FC",
            borderBottom: `1px solid ${theme.customColors?.border?.subtle || "#EEEEEE"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <PlayCircleOutlineIcon
              sx={{ fontSize: 28 }}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: theme.customColors?.text?.strong || "#1A1A2E",
                letterSpacing: "-0.01em",
              }}
            >
              {section?.title}
            </Typography>
          </Box>
          {isTrainingCompleted && (
            <Chip
              label="Completed"
              size="small"
              sx={{
                backgroundColor: theme.customColors.status.completed.bg,
                color: theme.palette.primary.light,
                fontWeight: "500",
              }}
            />
          )}
        </Box>

        <CardContent sx={{ px: 3, py: 2.5 }}>
          <TrainingInfoBox
            note="This video cannot be skipped or fast-forwarded. You may pause or rewind, but must watch the full video to continue. Switching tabs will restart the video."
          />

          {/* Video Player */}
          <Box
            sx={{
              borderRadius: "10px",
              overflow: "hidden",
              border: `1px solid ${theme.customColors?.border?.subtle || "#EEEEEE"}`,
              mb: 3,
            }}
          >
            <VideoPlayer
              fileUrl={videoUrl}
              showVideoSkipControls={false}
              resetOnWindowChange={true}
              onComplete={handleEnded}
            />
          </Box>

          {/* Acknowledgement Section */}
          {!isTrainingCompleted && (
            <Box
              sx={{
                mt: 1,
                pt: 2.5,
                borderTop: `1px solid ${theme.customColors?.border?.subtle || "#EEEEEE"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={(e) => setIsChecked(e.target.checked)}
                    disabled={!isCompleted}
                    sx={{
                      "&.Mui-checked": {
                        color: theme.palette.primary.main,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    I have reviewed and understood the training material
                    presented above.
                  </Typography>
                }
              />
              <Button
                variant="contained"
                disabled={!isChecked || !isCompleted || updating}
                onClick={handleUpdateStatus}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  px: 4,
                  py: 1,
                  borderRadius: "8px",
                }}
              >
                {updating ? "Submitting..." : "Acknowledge"}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default VideoPlayerPage;
